import { useLazyQuery } from '@apollo/client/react';
import { useEffect, useMemo } from 'react';

import {
  DirectVisitorDetailDialogProfileFragment,
  GetDirectVisitorMessagesDocument,
  GetDirectVisitorMessagesQuery,
  GetLineVisitorMessagesDocument,
  GetLineVisitorMessagesQuery,
  GetPatientMessagesDocument,
  GetPatientMessagesQuery,
  GetWebVisitorMessagesDocument,
  GetWebVisitorMessagesQuery,
  LineVisitorDetailDialogProfileFragment,
  PatientDetailDialogProfileFragment,
  WebVisitorDetailDialogProfileFragment,
} from '~/graphql/generated/graphql';

type Patient =
  | DirectVisitorDetailDialogProfileFragment
  | WebVisitorDetailDialogProfileFragment
  | PatientDetailDialogProfileFragment
  | LineVisitorDetailDialogProfileFragment
  | undefined;

const patientAllMessages = (data?: GetPatientMessagesQuery) => {
  if (!data || data?.all?.__typename !== 'Patient') {
    return undefined;
  }

  return data.all.medicationFollowupMessages.nodes;
};

const directVisitorAllMessages = (data?: GetDirectVisitorMessagesQuery) => {
  if (!data || data?.all?.__typename !== 'DirectVisitor') {
    return undefined;
  }

  return data.all.medicationFollowupMessages.nodes;
};

const webVisitorAllMessages = (data?: GetWebVisitorMessagesQuery) => {
  if (!data || data?.all?.__typename !== 'WebVisitor') {
    return undefined;
  }

  return data.all.medicationFollowupMessages.nodes;
};

const lineVisitorAllMessages = (data?: GetLineVisitorMessagesQuery) => {
  if (!data || data?.all?.__typename !== 'LineVisitor') {
    return undefined;
  }

  return data.all.medicationFollowupMessages.nodes;
};

export const useHasMessages = (patient: Patient) => {
  const medixsPatient = patient?.medixsPatient;
  const patientId = patient?.__typename === 'Patient' ? patient.id : undefined;
  const directVisitorId = patient?.__typename === 'DirectVisitor' ? patient?.id : undefined;
  const webVisitorId = patient?.__typename === 'WebVisitor' ? patient.id : undefined;
  const lineVisitorId = patient?.__typename === 'LineVisitor' ? patient.id : undefined;

  const [getPatientMessage, { loading: patientLoading, data: patientData }] = useLazyQuery(
    GetPatientMessagesDocument,
  );
  const [getDirectVisitorMessage, { loading: directVisitorLoading, data: directVisitorData }] =
    useLazyQuery(GetDirectVisitorMessagesDocument);
  const [getWebVisitorMessage, { loading: webVisitorLoading, data: webVisitorData }] = useLazyQuery(
    GetWebVisitorMessagesDocument,
  );
  const [getLineVisitorMessage, { loading: lineVisitorLoading, data: lineVisitorData }] =
    useLazyQuery(GetLineVisitorMessagesDocument);

  const hasPatientMessages = useMemo(() => {
    const data = patientAllMessages(patientData);
    return data && data.length > 0;
  }, [patientData]);
  const hasDirectVisitorMessages = useMemo(() => {
    const data = directVisitorAllMessages(directVisitorData);
    return data && data.length > 0;
  }, [directVisitorData]);
  const hasWebVisitorMessages = useMemo(() => {
    const data = webVisitorAllMessages(webVisitorData);
    return data && data.length > 0;
  }, [webVisitorData]);
  const hasLineVisitorMessages = useMemo(() => {
    const data = lineVisitorAllMessages(lineVisitorData);
    return data && data.length > 0;
  }, [lineVisitorData]);

  useEffect(() => {
    // medixsPatient はイベントが混在するため、メッセージ存在判定をサーバー側のフィールドに委ねる
    if (medixsPatient) {
      return;
    }

    if (patientId) {
      getPatientMessage({
        variables: {
          id: patientId,
        },
      });
    }

    if (directVisitorId) {
      getDirectVisitorMessage({
        variables: {
          id: directVisitorId,
        },
      });
    }

    if (webVisitorId) {
      getWebVisitorMessage({
        variables: {
          id: webVisitorId,
        },
      });
    }

    if (lineVisitorId) {
      getLineVisitorMessage({
        variables: {
          id: lineVisitorId,
        },
      });
    }
  }, [
    medixsPatient,
    directVisitorId,
    getDirectVisitorMessage,
    getPatientMessage,
    getWebVisitorMessage,
    getLineVisitorMessage,
    patientId,
    webVisitorId,
    lineVisitorId,
  ]);

  if (medixsPatient) {
    return {
      loading: false,
      hasMessages: medixsPatient.hasMedicationFollowupMessages,
    };
  }

  const hasMessages = !!(
    hasPatientMessages ||
    hasDirectVisitorMessages ||
    hasWebVisitorMessages ||
    hasLineVisitorMessages
  );

  return {
    loading: patientLoading || directVisitorLoading || webVisitorLoading || lineVisitorLoading,
    hasMessages,
  };
};
